import React, { useContext, useEffect } from 'react';
import { FormControlLabel, RadioGroup, Radio } from '@mui/material';
import { connect } from 'react-redux';
import { Form, Field } from 'react-final-form';
import {
  kycGotoNextPage as kycGotoNextPageRoutine,
  kycGotoPrevPage as kycGotoPrevPageRoutine,
  kycSetDraftHasUBO,
  kycSubmitCustomerDetails as kycSubmitCustomerDetailsRoutine,
} from 'spa/actions/VerificationActions';
import VerificationConstants, { KYCPages } from 'spa/constants/VerificationConstants';
import ActionButtons from 'spa/components/CustomerVerification/V2/ActionButtons';
import {
  customerKycHasPrevPagesSelector,
  customerKycLoadingUiSelector,
  customerKycDraftCompanyDetailsSelector,
  customerKycDraftHasUBOSelector,
  customerKycSubmitLoadingSelector,
  customerRiskAssessmentAnswersSelector,
} from 'spa/selectors/CustomerSelectors';
import { KYCContext } from 'spa/context/KYCContext';
import Heading from './Heading';

const requiredBoolean = (value) => (typeof value === 'boolean' ? undefined : 'Required');

const booleanToRadioValue = (value) => {
  if (value === true) {
    return 'true';
  }
  if (value === false) {
    return 'false';
  }
  return '';
};

const HasUBOForm = ({
  draftCompanyDetails,
  draftHasUBO,
  riskAssessmentAnswers,
  isNavigating,
  isSubmitting,
  hasPrevPages,
  loadPrevPage,
  gotoNextPage,
  setDraftHasUBO,
  submitCustomerDetails,
}) => {
  const kycContext = useContext(KYCContext);

  useEffect(() => {
    if (!draftCompanyDetails) {
      gotoNextPage({
        nextPage: KYCPages.COMPANY_DETAILS_FORM,
        forceNextPage: true,
        purgeHistory: true,
        kycContext,
      });
    }
  }, [draftCompanyDetails, gotoNextPage, kycContext]);

  const onFormSubmit = (values, form) => {
    const registeredFields = form.getRegisteredFields();
    setDraftHasUBO({
      values,
      registeredFields,
    });

    if (values.has_significant_individual_owner === false) {
      const draftValues = {
        ...((draftCompanyDetails && draftCompanyDetails.values) || {}),
        ...values,
      };
      const draftRegisteredFields = [
        ...new Set([
          ...((draftCompanyDetails && draftCompanyDetails.registeredFields) || []),
          ...registeredFields,
        ]),
      ];
      submitCustomerDetails({
        formName: VerificationConstants.UBO_QUESTIONS_FORM,
        registeredFields: draftRegisteredFields,
        values: draftValues,
        kycContext,
      });
      return;
    }

    gotoNextPage({
      nextPage: KYCPages.STOCK_EXCHANGE_FORM,
      forceNextPage: true,
      kycContext,
    });
  };

  return (
    <Form
      onSubmit={onFormSubmit}
      initialValues={{
        ...riskAssessmentAnswers,
        ...((draftHasUBO && draftHasUBO.values) || {}),
      }}
    >
      {({ handleSubmit, hasValidationErrors, values }) => (
        <form onSubmit={handleSubmit} name={VerificationConstants.UBO_QUESTIONS_FORM}>
          <Heading text="Does any individual own more than 25% of this company?" hasDivider />
          <Field name="has_significant_individual_owner" validate={requiredBoolean}>
            {({ input }) => (
              <RadioGroup
                name={input.name}
                value={booleanToRadioValue(input.value)}
                onChange={(event) => input.onChange(event.target.value === 'true')}
                onBlur={input.onBlur}
                onFocus={input.onFocus}
              >
                <FormControlLabel value="true" control={<Radio />} label="Yes" />
                <FormControlLabel value="false" control={<Radio />} label="No" />
              </RadioGroup>
            )}
          </Field>
          <ActionButtons
            secondaryText="Back"
            primaryText={
              values.has_significant_individual_owner === false ? 'Save and Next' : 'Next'
            }
            isLoading={isNavigating || isSubmitting}
            isDisabled={hasValidationErrors}
            type="submit"
            withBack={hasPrevPages}
            onSecondaryClick={() => loadPrevPage()}
          />
        </form>
      )}
    </Form>
  );
};

const mapStateToProps = (state) => ({
  draftCompanyDetails: customerKycDraftCompanyDetailsSelector(state),
  draftHasUBO: customerKycDraftHasUBOSelector(state),
  riskAssessmentAnswers: customerRiskAssessmentAnswersSelector(state),
  isNavigating: customerKycLoadingUiSelector(state),
  isSubmitting: customerKycSubmitLoadingSelector(state),
  hasPrevPages: customerKycHasPrevPagesSelector(state),
});

const mapDispatchToProps = (dispatch) => ({
  loadPrevPage: () => dispatch(kycGotoPrevPageRoutine.trigger()),
  gotoNextPage: (payload) => dispatch(kycGotoNextPageRoutine.trigger(payload)),
  setDraftHasUBO: (payload) => dispatch(kycSetDraftHasUBO(payload)),
  submitCustomerDetails: (payload) => dispatch(kycSubmitCustomerDetailsRoutine.trigger(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(HasUBOForm);
